import { format } from "date-fns";
import { useMemo } from "react";
import { Theme, useMediaQuery } from "@mui/material";

import { PaymentSummaryPricing } from "@covertree/ui";
import {
  getDiscountsFromUnitPricing,
  selectCurrentlySelectedQuotePricing,
  selectCurrentOfferPricing,
  selectPolicy,
  selectShowMailingAddressReminder,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { PolicyUsage } from "@covertree/new-business-graphql";

interface Props {
  useNewOffersAndSelectedQuotePricing: boolean;
}

export const usePolicySummary = ({
  useNewOffersAndSelectedQuotePricing,
}: Props) => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  const { startDate, policyholder, policyLevelCoverages, units } =
    useNewBusinessSelector(selectPolicy)!;
  const unitPricing = useNewOffersAndSelectedQuotePricing
    ? useNewBusinessSelector(selectCurrentlySelectedQuotePricing)
    : useNewBusinessSelector(selectCurrentOfferPricing);

  const showMailingAddressReminder = useNewBusinessSelector(
    selectShowMailingAddressReminder
  );

  const pricing: PaymentSummaryPricing = useMemo(() => {
    const { discounts, discountsTotal } =
      getDiscountsFromUnitPricing(unitPricing);

    if (!unitPricing) {
      return {
        discounts: [],
        totalCost: 0,
        summary: [],
      };
    }

    return {
      discounts,
      totalCost: unitPricing.totalDue,
      summary: [
        {
          name: "Starting Premium",
          value: unitPricing.grossPremium + discountsTotal,
        },
        {
          name: "Fees",
          value: unitPricing.grossFees,
        },
        {
          name: "Taxes",
          value: unitPricing.grossTaxes,
        },
      ],
    };
  }, [unitPricing]);

  const formattedStartDate = useMemo(
    () => format(new Date(`${startDate} 00:00:00`), "MMMM do, yyyy"),
    [startDate]
  );

  const formattedPolicyUsage = useMemo(() => {
    if (!units[0]) return;

    if (units[0].usage === PolicyUsage.Rental) return "landlord";

    return units[0].usage.toLowerCase();
  }, [units]);

  return {
    isDesktop,
    pricing,
    formattedStartDate,
    policyholder,
    units,
    policyLevelCoverages,
    showMailingAddressReminder,
    formattedPolicyUsage,
  };
};
